import { useEffect, useRef, type RefObject } from 'react';

/* ===========================================================================
 * HOW A NON-MODAL POPUP CLOSES — an outside press, or Escape. Once, here.
 *
 * Eight popups wrote this block for themselves (the row ⋯ menu, the filter
 * panel, the calendar, the month picker, the notification panel, Save view,
 * the employee picker, the report search bar), and the copies had drifted in
 * every way the block can drift:
 *
 *  • BUBBLE vs CAPTURE. Half listened in the bubble phase, so a press on
 *    anything that stopped propagation on its way up — a table row's own click
 *    handler — never reached them, and the popup stayed open.
 *  • ESCAPE LEAKED. Half let Escape carry on to the dialog behind them, so
 *    closing a picker inside a form dialog closed the form too.
 *  • PORTALLED CHILDREN. Only the filter panel honoured `data-portal-surface`.
 *    The report search bar did not, and its period clause holds date fields
 *    whose calendar is portalled to <body> — so picking a date read as a press
 *    OUTSIDE the bar and shut it on the very choice it was opened to make.
 *
 * The rules, now held in one place:
 *  1. Capture phase, on `document`, for both events.
 *  2. A press inside any of `inside` — or on any `[data-portal-surface]`, the
 *     marker a portalled surface carries to say "I belong to whatever opened
 *     me" — is not outside.
 *  3. Escape closes the MOST RECENTLY OPENED popup only, and is stopped, so
 *     nothing behind it closes too. Every copy listened on `document`, and
 *     `stopPropagation` does not stop other listeners on the same node — so a
 *     calendar opened inside the filter panel took the panel down with it.
 *     `escape: false` for a popup whose Escape is a field's own key handling
 *     (a combobox's input), which then owns that decision.
 *
 * `how` tells the caller which happened, because the two differ in one way
 * that matters: Escape is a keyboard round trip and should put the caret back
 * on the trigger; an outside press should NOT — the reader is already looking
 * somewhere else, and yanking focus back is the rudest thing a popup can do.
 * ======================================================================== */

export type DismissReason = 'escape' | 'outside';

export interface DismissOptions {
  /** Close on Escape too. Default true. */
  escape?: boolean;
}

/** Open popups that answer Escape, in opening order. */
const escapeStack: object[] = [];

export function useDismiss(
  open: boolean,
  inside: ReadonlyArray<RefObject<HTMLElement | null>>,
  onDismiss: (how: DismissReason) => void,
  { escape = true }: DismissOptions = {}
): void {
  // Latest values through refs, so a caller passing an inline array or an
  // inline callback does not re-subscribe both listeners on every render.
  const insideRef = useRef(inside);
  const dismissRef = useRef(onDismiss);
  insideRef.current = inside;
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) return undefined;
    const entry = {};
    if (escape) escapeStack.push(entry);
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      if (escapeStack[escapeStack.length - 1] !== entry) return;
      e.stopPropagation();
      dismissRef.current('escape');
    }
    function onPointerDown(e: Event): void {
      const node = e.target;
      if (!(node instanceof Node)) return;
      if (insideRef.current.some((r) => r.current?.contains(node) === true)) return;
      if (node instanceof Element && node.closest('[data-portal-surface]') !== null) return;
      dismissRef.current('outside');
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    if (escape) document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      if (escape) {
        document.removeEventListener('keydown', onKey, true);
        escapeStack.splice(escapeStack.indexOf(entry), 1);
      }
    };
  }, [open, escape]);
}
